"use client";

import {
	deriveAddToCartState,
	isCartDisabledByState
} from "@/composites/cart/AddToCartButton";
import { type CatalogProduct, type OnlineStatus } from "@/domain/catalog";
import { isPriceVisible } from "@/domain/catalog/online-status";
import { useEffect, type ReactNode } from "react";
import { selectHighlightTags } from "@/composites/product/ProductHighlightTag";
import { selectProductMessages } from "@/composites/product/ProductMessages";
import {
	ProductSummaryView,
	type ProductSummaryViewProps
} from "./ProductSummaryView";

export interface ProductSummaryProps {
	product: CatalogProduct;
	onError?: (message: string) => void;
	onSuccess?: () => void;
	addToList?: ReactNode;
	className?: string;
	quantity: number;
	onQuantityChange: (quantity: number) => void;
	addToCart: {
		onClick: () => void | Promise<void>;
		loading: boolean;
		error: string | null;
	};
	paymentMessage?: ReactNode;
}

/**
 * Wired ProductSummary — derives every input to `ProductSummaryView` from
 * a `CatalogProduct`, quantity state, and the AddToCart handler props.
 * Selects the highlight tags and message rows the View renders, resolving
 * each date window at render time.
 */
export function ProductSummary({
	product,
	onError,
	addToList,
	className,
	quantity,
	onQuantityChange,
	addToCart,
	paymentMessage
}: ProductSummaryProps) {
	useEffect(() => {
		if (addToCart.error && onError) onError(addToCart.error);
	}, [addToCart.error, onError]);

	const showPrice = isPriceVisible(product.onlineStatus);
	const display = product.salePrice;
	const list = product.listPrice;
	const isOnSale =
		typeof display === "number" &&
		typeof list === "number" &&
		display < list;

	const ratingProp: ProductSummaryViewProps["rating"] =
		typeof product.starsRating === "number" &&
		typeof product.numRatings === "number" &&
		product.numRatings > 0
			? {
					value: product.starsRating,
					count: product.numRatings,
					reviewsHref: "#reviews"
				}
			: undefined;

	const availabilityTag = computeAvailabilityTag(product.onlineStatus);

	const addToCartState = deriveAddToCartState(product, {
		loading: addToCart.loading,
		error: addToCart.error
	});

	const cartDisabled = isCartDisabledByState(addToCartState.kind);
	const canAddToLists =
		product.onlineStatus !== "discontinued" &&
		product.onlineStatus !== "inactive";

	const highlightTags = selectHighlightTags(product.productTags);
	const messages = selectProductMessages(product.productMessages);

	return (
		<ProductSummaryView
			title={product.title}
			sku={product.sku}
			rating={ratingProp}
			pricing={
				showPrice
					? {
							display,
							strikeThrough: isOnSale ? list : undefined,
							halfPriceCombo: product.halfPriceComboEligible
						}
					: undefined
			}
			highlightTags={highlightTags}
			messages={messages}
			availabilityTag={availabilityTag}
			quantity={
				cartDisabled
					? undefined
					: {
							value: quantity,
							onChange: onQuantityChange,
							min: 1,
							max: 99
						}
			}
			addToCart={{
				state: addToCartState,
				onClick: addToCart.onClick
			}}
			addToList={canAddToLists ? addToList : undefined}
			paymentMessage={showPrice ? paymentMessage : undefined}
			className={className}
		/>
	);
}

function computeAvailabilityTag(
	status: OnlineStatus
): { label: string; color: "error" | "grey" } | undefined {
	switch (status) {
		case "pre-launch":
			return { label: "Coming Soon", color: "grey" };
		case "out-of-stock":
			return { label: "Out of Stock", color: "error" };
		case "temporarily-unavailable":
			return { label: "Currently Unavailable", color: "grey" };
		case "discontinued":
		case "inactive":
			return { label: "Discontinued", color: "grey" };
		default:
			return undefined;
	}
}
